import { useQuery } from '@tanstack/react-query';

import {
  GET_SHOPPER_CONFIG_QUERY_KEY,
  GET_SHOPPER_DEBUG_ACTIVE_PROMPTS,
  GET_SHOPPER_PROMPT_OVERRIDES_QUERY_KEY,
} from 'components/shopper/constants';
import {
  getShopConfigValues,
  getShopperDebugActivePrompts,
  getShopPromptOverrides,
} from 'components/shopper/shopperClient';
import { ConfigData, ShopperDebugActivePromptsResponse } from '../../types';

export const useShopperConfig = () => {
  return useQuery({
    queryKey: [GET_SHOPPER_CONFIG_QUERY_KEY],
    queryFn: getShopConfigValues,
  });
};

export const useGetShopperDebugActivePrompts = () =>
  useQuery<ShopperDebugActivePromptsResponse, Error>({
    queryKey: [GET_SHOPPER_DEBUG_ACTIVE_PROMPTS],
    queryFn: async () => {
      const data = await getShopperDebugActivePrompts();

      if (data && typeof data === 'object' && data.message === 'Forbidden') {
        throw new Error('Forbidden');
      }

      return data;
    },
  });

export const useShopperPromptOverrides = () =>
  useQuery({
    queryKey: [GET_SHOPPER_PROMPT_OVERRIDES_QUERY_KEY],
    queryFn: getShopPromptOverrides,
  });

export const useShopperLivePolling = (
  polling: boolean,
  stopPolling: () => void,
  timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
) =>
  useQuery<ConfigData, unknown>({
    queryKey: [GET_SHOPPER_CONFIG_QUERY_KEY],
    queryFn: getShopConfigValues,
    enabled: polling,
    refetchInterval: (data: ConfigData | undefined) => {
      if (data?.is_live_mode) {
        // Clear the external timeout and cease polling
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        stopPolling();
        return false;
      }
      // otherwise continue every 2 seconds
      return 2000;
    },
    refetchOnWindowFocus: false,
  });
